import React from "react";
import { Link } from "react-router-dom";
import { Activity, Send, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    MODULE_GUIDES,
    QUICK_LINKS,
    WORKFLOW_STEPS,
} from "./constants";
import {
    ActivityTimeline,
    KpiCard,
    PanelCard,
    PriorityQueue,
    QuickLinkGrid,
    SectionIntro,
    StatPill,
    WorkflowStrip,
} from "./ui";

export function OzetPanel({
    ozet,
    evraklar,
    aktiviteler,
    bekleyenMesajDosyalar,
    yanitDosyaId,
    setYanitDosyaId,
    yanitMetin,
    setYanitMetin,
    handleYanit,
    busyKey,
    onTabChange,
}) {
    const bekleyenEvrak = evraklar.filter((e) => e.onay_durumu === "BEKLIYOR");

    return (
        <div className="space-y-5">
            <PanelCard>
                <SectionIntro
                    icon={Zap}
                    title="Bugünkü öncelikler"
                    description="Sistem bekleyen işleri önem sırasına göre listeler. Satıra tıklayarak ilgili modüle geçin."
                />
                <PriorityQueue
                    items={ozet?.oncelikler}
                    onNavigate={onTabChange}
                />
            </PanelCard>

            <PanelCard>
                <SectionIntro
                    title="Operasyon akışı"
                    description="Müvekkil portalından tahkime kadar tipik iş sırası — adımlara tıklayarak modül açılır."
                />
                <WorkflowStrip steps={WORKFLOW_STEPS} onStepClick={onTabChange} />
            </PanelCard>

            <div className="grid lg:grid-cols-2 gap-4">
                <PanelCard>
                    <SectionIntro
                        title="Son onay bekleyen evraklar"
                        description="En yeni portal yüklemeleri — hızlıca onay kuyruğuna geçin."
                        actions={
                            bekleyenEvrak.length > 0 ? (
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="border-white/15 text-xs"
                                    onClick={() => onTabChange("onay")}
                                >
                                    Tümünü gör
                                </Button>
                            ) : null
                        }
                    />
                    {bekleyenEvrak.length === 0 ? (
                        <p className="text-sm text-slate-500">Bekleyen portal evrakı yok.</p>
                    ) : (
                        <ul className="space-y-2">
                            {bekleyenEvrak.slice(0, 6).map((ev) => (
                                <li
                                    key={ev.id}
                                    className="flex items-center justify-between gap-3 rounded-lg border border-white/8 bg-[#0B0F19]/50 px-3 py-2.5"
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm text-white truncate">
                                            {ev.evrak_turu_label}
                                        </div>
                                        <div className="text-[11px] text-slate-500 truncate">
                                            {ev.dosya_no} · {ev.muvekkil_ad}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="text-[10px] text-[#D4AF37] shrink-0 hover:underline"
                                        onClick={() => onTabChange("onay")}
                                    >
                                        İncele
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </PanelCard>

                <PanelCard>
                    <SectionIntro
                        title="Hızlı mesaj yanıtı"
                        description="Yanıt bekleyen dosyalara doğrudan buradan yazın; müvekkil portalında görünür."
                    />
                    <form onSubmit={handleYanit} className="space-y-3">
                        <select
                            value={yanitDosyaId}
                            onChange={(e) => setYanitDosyaId(e.target.value)}
                            className="w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                        >
                            <option value="">Dosya seçin</option>
                            {bekleyenMesajDosyalar.map((m) => (
                                <option key={m.dosya_id} value={m.dosya_id}>
                                    {m.muvekkil_ad} · T.C. {m.muvekkil_tc || "—"} · {m.dosya_no}
                                </option>
                            ))}
                        </select>
                        <Textarea
                            value={yanitMetin}
                            onChange={(e) => setYanitMetin(e.target.value)}
                            placeholder="Müvekkile kısa ve net yanıt…"
                            rows={3}
                            className="bg-[#0B0F19] border-white/10 text-white"
                        />
                        <Button
                            type="submit"
                            size="sm"
                            disabled={busyKey === "yanit"}
                            className="bg-[#8b1f2f] hover:bg-[#6b1825]"
                        >
                            <Send className="h-4 w-4 mr-1" /> Yanıt gönder
                        </Button>
                    </form>
                </PanelCard>
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                <PanelCard className="lg:col-span-2">
                    <SectionIntro
                        icon={Activity}
                        title="Son portal aktivitesi"
                        description="Evrak, durum ve ödeme bildirimlerinin son kayıtları."
                        actions={
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="text-xs text-[#D4AF37]"
                                onClick={() => onTabChange("aktivite")}
                            >
                                Tüm akış
                            </Button>
                        }
                    />
                    <ActivityTimeline items={aktiviteler.slice(0, 5)} />
                </PanelCard>

                <PanelCard>
                    <SectionIntro title="Durum özeti" />
                    <div className="grid grid-cols-2 gap-2">
                        <StatPill
                            label="Portal Akıl"
                            value={ozet?.portal_akil_aktif ? "Açık" : "Kapalı"}
                            tone={ozet?.portal_akil_aktif ? "ok" : "warn"}
                        />
                        <StatPill label="Tahkim adayı" value={ozet?.tahkim_aday_dosya ?? "—"} />
                        <StatPill label="Bugün yükleme" value={ozet?.evrak_portal_bugun ?? 0} />
                        <StatPill label="Açık dosya" value={ozet?.acik_dosya ?? 0} />
                    </div>
                </PanelCard>
            </div>

            <PanelCard>
                <SectionIntro
                    title="Hızlı erişim"
                    description="Sık kullanılan modüllere tek tıkla geçiş."
                />
                <QuickLinkGrid links={QUICK_LINKS} />
            </PanelCard>
        </div>
    );
}

export function RehberPanel({ onTabChange }) {
    return (
        <div className="space-y-5">
            <PanelCard>
                <SectionIntro
                    title="MBS Müvekkil operasyon rehberi"
                    description="Bu merkez müvekkil portalı ile ofis paneli arasındaki köprüdür. Aşağıdaki modüller birbirini tamamlar."
                />
                <WorkflowStrip steps={WORKFLOW_STEPS} onStepClick={onTabChange} />
            </PanelCard>

            <div className="grid md:grid-cols-2 gap-4">
                {MODULE_GUIDES.map((mod) => {
                    const Icon = mod.icon;
                    return (
                        <PanelCard key={mod.id}>
                            <div className="flex items-start gap-3 mb-3">
                                <div className="rounded-lg bg-[#D4AF37]/10 p-2">
                                    <Icon className="h-5 w-5 text-[#D4AF37]" />
                                </div>
                                <div>
                                    <h3 className="font-display text-white">{mod.title}</h3>
                                    <button
                                        type="button"
                                        className="text-[11px] text-[#D4AF37] mt-1 hover:underline"
                                        onClick={() => onTabChange(mod.id)}
                                    >
                                        Modüle git →
                                    </button>
                                </div>
                            </div>
                            <ul className="space-y-2">
                                {mod.bullets.map((b) => (
                                    <li
                                        key={b}
                                        className="text-xs text-slate-400 leading-relaxed flex gap-2"
                                    >
                                        <span className="text-[#D4AF37] shrink-0">•</span>
                                        {b}
                                    </li>
                                ))}
                            </ul>
                        </PanelCard>
                    );
                })}
            </div>

            <PanelCard>
                <SectionIntro title="Kısayol linkleri" />
                <QuickLinkGrid links={QUICK_LINKS} />
                <p className="text-[11px] text-slate-500 mt-4">
                    Müvekkil portal adresi:{" "}
                    <a
                        href="https://mbshukuk.com/panel/dashboard/"
                        className="text-[#D4AF37] hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        mbshukuk.com/panel/dashboard/
                    </a>
                </p>
            </PanelCard>
        </div>
    );
}

export function KpiGrid({ ozet, onTabChange }) {
    if (!ozet) return null;
    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-2 md:gap-3">
            <KpiCard
                label="Onay bekleyen"
                value={ozet.evrak_portal_bekleyen}
                hint="Portal evrak kuyruğu"
                accent="amber"
                onClick={() => onTabChange("onay")}
            />
            <KpiCard
                label="Yanıt bekleyen"
                value={ozet.mesaj_bekleyen_dosya}
                hint="Müvekkil mesajı"
                accent="amber"
                onClick={() => onTabChange("mesaj")}
            />
            <KpiCard
                label="Eksik evraklı"
                value={ozet.eksik_evrakli_dosya}
                hint="7'li set eksik"
                accent="red"
                onClick={() => onTabChange("eksik")}
            />
            <KpiCard
                label="Bugün yüklenen"
                value={ozet.evrak_portal_bugun}
                hint="Son 24 saat"
                onClick={() => onTabChange("onay")}
            />
            <KpiCard
                label="Reddedilen"
                value={ozet.evrak_portal_reddedilen}
                accent="red"
                onClick={() => onTabChange("onay")}
            />
            <KpiCard label="Müvekkil mesajı" value={ozet.mesaj_muvekkil_toplam} onClick={() => onTabChange("mesaj")} />
            <KpiCard label="Açık dosya" value={ozet.acik_dosya} />
            <KpiCard
                label="Tahkim adayı"
                value={ozet.tahkim_aday_dosya}
                hint="Başvuru / tahkim"
                onClick={() => onTabChange("tahkim")}
            />
        </div>
    );
}
